/**
 * Dismiss / Escalate dialog for Activity findings.
 *
 * Fix D UI (2026-05-28) - backend PATCH endpoint shipped on the same
 * day; this is the operator-facing surface for it.
 *
 * Used by UEBA + Sonar finding pages. The status (dismissed | escalated)
 * is fixed by the caller - the dialog just collects the reason and
 * dispatches the right API call.
 *
 * Reason is required + min 3 chars + max 1000 chars (mirrors the
 * server-side Zod PatchFindingStatusSchema). Submitting an invalid
 * reason is blocked at the UI layer so the user gets immediate
 * feedback rather than a 400.
 */
import { useState } from 'react';
import { Loader2, ShieldCheck, ShieldX } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { Button } from '@/components/ui/button';

export type FindingTransition = 'dismissed' | 'escalated';

interface FindingStatusDialogProps {
  open: boolean;
  status: FindingTransition;
  /** Short description of the finding being acted on (actor + metric). */
  context: string;
  /**
   * Performs the actual PATCH. Caller passes patchUebaFindingStatus or
   * patchSonarFindingStatus partially-applied with the finding id.
   */
  onSubmit: (reason: string) => Promise<void>;
  onClose: () => void;
}

export function FindingStatusDialog({
  open,
  status,
  context,
  onSubmit,
  onClose,
}: FindingStatusDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleNoun = status === 'dismissed' ? 'Dismiss' : 'Escalate';
  const verb = status === 'dismissed' ? 'dismiss' : 'escalate';
  const helpText =
    status === 'dismissed'
      ? 'Recording a false-positive. The reason is captured in the audit-chain and shown to the regulator on review - be specific (e.g. "scheduled maintenance window 02:00-03:00 IST; matches change-request CR-4421").'
      : 'Escalating to incident-response. The reason is captured in the audit-chain. Include the threat-intel reference or playbook trigger (e.g. "matches Aadhaar bulk-export TTP; opening DDR session-kill playbook").';

  const reasonTooShort = reason.trim().length > 0 && reason.trim().length < 3;
  const reasonTooLong = reason.length > 1000;
  const reasonInvalid = reason.trim().length < 3 || reasonTooLong;

  async function handleSubmit(): Promise<void> {
    if (reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
      // Reset for next open + close
      setReason('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed - try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !submitting) {
          setReason('');
          setError(null);
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status === 'dismissed' ? (
              <ShieldX className="h-5 w-5 text-amber-500" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-red-500" />
            )}
            {titleNoun} finding
          </DialogTitle>
          <DialogDescription className="text-xs">{context}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">{helpText}</p>
          <div>
            <label
              htmlFor="dismiss-reason"
              className="block text-xs font-medium mb-1.5 text-foreground"
            >
              Reason
              <span className="ml-1 text-muted-foreground font-normal">
                (3 - 1000 chars, captured in audit chain)
              </span>
            </label>
            <textarea
              id="dismiss-reason"
              autoFocus
              rows={4}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Enter submits
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !reasonInvalid) {
                  void handleSubmit();
                }
              }}
              placeholder={
                status === 'dismissed'
                  ? 'e.g. False-positive - actor was running scheduled batch...'
                  : 'e.g. Confirmed exfil pattern - matches threat-intel feed XYZ...'
              }
              className="w-full min-h-[96px] rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
              <span>
                {reasonTooShort ? (
                  <span className="text-amber-600">Min 3 chars required</span>
                ) : reasonTooLong ? (
                  <span className="text-red-600">Max 1000 chars exceeded</span>
                ) : (
                  <span>Ctrl+Enter to submit</span>
                )}
              </span>
              <span>{reason.length} / 1000</span>
            </div>
          </div>

          {error ? (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="ghost" onClick={onClose} disabled={submitting} size="sm">
            Cancel
          </Button>
          <Button
            variant={status === 'escalated' ? 'destructive' : 'default'}
            onClick={() => void handleSubmit()}
            disabled={reasonInvalid || submitting}
            size="sm"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : null}
            {titleNoun} (Ctrl+Enter)
            <span className="sr-only">- {verb} this finding with the supplied reason</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
